import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

interface KeyboardShortcutsModalProps {
  open: boolean;
  onClose: () => void;
}

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({ open, onClose }) => {
  const shortcuts = [
    { keys: ['Shift', '/'], action: 'Open shortcuts panel' },
    { keys: ['G', 'D'], action: 'Go to Dashboard' },
    { keys: ['G', 'A'], action: 'Go to Applications' },
    { keys: ['N'], action: 'New RTI Application' },
    { keys: ['G', 'P'], action: 'Go to Profile' },
    { keys: ['G', 'S'], action: 'Go to Settings' },
    { keys: ['?'], action: 'Open shortcuts' },
    { keys: ['/'], action: 'Focus search bar' },
    { keys: ['T'], action: 'Toggle theme' },
  ];

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <QuestionMarkCircleIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                    <Dialog.Title className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Keyboard Shortcuts
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {shortcuts.map((s, idx) => (
                      <div key={idx} className="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-700 p-3">
                        <span className="text-sm text-gray-700 dark:text-gray-200">{s.action}</span>
                        <div className="flex items-center gap-1">
                          {s.keys.map((k, i) => (
                            <span key={i} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-xs font-medium text-gray-800 dark:text-gray-100">
                              {k}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                    Tip: You can also use the navigation links to explore features.
                  </div>
                </div>
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 text-right">
                  <button
                    onClick={onClose}
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default KeyboardShortcutsModal;
